import { FeesRepo } from "@services/fees.repo";
import * as _ from "lodash";
import * as moment from "moment";
import * as echarts from "echarts";
import { exportToCSV } from "@common/utils/exportToCSV";
import { FeeCalculationType } from "../../fees";
import { extractErrorMessage } from "@common/utils/errorUtils";

class ClientFeesController implements angular.IOnInit {
  public feesByClientLimit: number = 10;
  public topClientFeeChart: any;
  public topClientFeeChartOptions: any;
  public isLoadingClientFees: boolean = false;
  public topClientFees: Array<any>;
  public bottomClientFees: Array<any>;
  private pendingRequests: number = 0;
  public clientFeeDate: Date;
  public bottomClientFeeChart: any;
  public bottomClientFeeChartOptions: any;
  public topClientFeesGridOptions: any;
  public bottomClientFeesGridOptions: any;
  public sortTopBottomClientFeesByNav: boolean = false;
  public selectedFeeCalculationTypeClientFees: FeeCalculationType = FeeCalculationType.COMBINED;
  public showClientFee: boolean = true;

  // Bindings from parent
  public lastMonth: Date;
  public selectedCurrency: string;
  public feeCalulationTypes: Array<any>;
  public exportClientFeesRef: any;

  /** @ngInject */
  constructor(
    private feesRepo: FeesRepo,
    private notifyjs: any,
    private $scope: angular.IScope
  ) {}

  private setLoading(loading: boolean) {
    if (loading) {
      this.pendingRequests++;
    } else {
      this.pendingRequests = Math.max(0, this.pendingRequests - 1);
    }
    this.isLoadingClientFees = this.pendingRequests > 0;
    if (this.exportClientFeesRef) {
      this.exportClientFeesRef.isLoading = this.isLoadingClientFees;
    }
  }

  $onInit() {
    // Expose export method to parent
    if (this.exportClientFeesRef) {
      this.exportClientFeesRef.exportClientFeesToCSV = this.exportClientFeesToCSV;
    }

    this.topClientFeeChart = echarts.init(
      document.getElementById("top-client-fee-value-chart")
    );
    this.$scope.$watch(
      () =>
        this.topClientFeeChart.getDom().parentElement.parentElement
          .clientHeight,
      this.topClientFeeChart.resize()
    );

    this.bottomClientFeeChart = echarts.init(
      document.getElementById("bottom-client-fee-value-chart")
    );
    this.$scope.$watch(
      () =>
        this.bottomClientFeeChart.getDom().parentElement.parentElement
          .clientHeight,
      this.bottomClientFeeChart.resize()
    );

    this.topClientFeesGridOptions = this.$scope["topClientFeesGridOptions"] = {
      data: [],
      urlSync: false,
    };

    this.bottomClientFeesGridOptions = this.$scope[
      "bottomClientFeesGridOptions"
    ] = {
      data: [],
      urlSync: false,
    };

    // Watch for currency changes and update fees
    this.$scope.$watch(
      () => this.selectedCurrency,
      (newVal, oldVal) => {
        if (newVal !== oldVal && newVal) {
          this.updateClientFees();
        }
      }
    );

    this.updateClientFees();
  }

  updateClientFees = () => {
    this.getTopClientFeesByLimit();
    this.getBottomClientFeesByLimit();
  };

  formatDate(selectedDate) {
    return moment(selectedDate).endOf("month").format("YYYY-MM");
  }

  getTopClientFeesByLimit = () => {
    this.setLoading(true);
    this.feesRepo
      .getClientFeesByLimit(
        this.clientFeeDate,
        this.feesByClientLimit,
        true,
        this.selectedCurrency,
        this.selectedFeeCalculationTypeClientFees,
        this.sortTopBottomClientFeesByNav
      )
      .then((res) => {
        this.topClientFees = res.data as Array<any>;
        this.getTopClientFeeGraph();
        this.topClientFeesGridOptions.data = this.topClientFees;
      })
      .catch((err) => {
        this.notifyjs("Failed to get top client fees " + extractErrorMessage(err));
      })
      .finally(() => {
        this.setLoading(false);
      });
  };

  getTopClientFeeGraph = () => {
    let clientNames = this.topClientFees.map((client) => client.clientName);
    let feeValues;
    if (this.sortTopBottomClientFeesByNav) {
      feeValues = this.topClientFees.map((client) =>
        client.marketValue.toFixed(2)
      );
    } else {
      feeValues = this.topClientFees.map((client) =>
        client.feeValue.toFixed(2)
      );
    }

    this.topClientFeeChartOptions = {
      title: {
        text: `Top ${clientNames.length} Client ${
          this.sortTopBottomClientFeesByNav ? "NAVs" : "Fees"
        }`,
        textStyle: {
          fontFamily: "Seravek",
        },
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        formatter: (params) => {
          let feeParam = this.topClientFees.filter(
            (feeParam) => feeParam.clientName == params[0].name
          )[0];
          return `Client : ${feeParam.clientName} <br>
                    Client Number : ${feeParam.clientNumber} <br>
                    Fee Value : ${feeParam.feeValue.toLocaleString("en-EN", {
                      style: "currency",
                      currency: this.selectedCurrency,
                    })} <br>
                    NAV : ${feeParam.marketValue.toLocaleString("en-EN", {
                      style: "currency",
                      currency: this.selectedCurrency,
                    })} `;
        },
      },

      grid: {
        left: "3%",
        right: "4%",
        bottom: "3%",
        containLabel: true,
      },
      toolbox: {
        feature: {
          saveAsImage: {
            title: "Save as Image",
          },
          dataZoom: {
            yAxisIndex: "none",
            title: {
              zoom: "Zoom In",
              back: "Back",
            },
          },
          restore: {
            title: "Restore",
          },
        },
      },
      xAxis: {
        type: "category",
        boundaryGap: true,
        data: clientNames,
        barGap: 0,
        axisLabel: {
          rotate: 45,
        },
      },
      yAxis: [
        {
          type: "value",
          name: `${this.sortTopBottomClientFeesByNav ? "NAVs" : "Fees"} `,

          axisLabel: {
            formatter: (params) => {
              return params > 0
                ? `${this.selectedCurrency} ${this.normaliseNumbers(params)}`
                : "";
            },
          },
        },
      ],
      series: [
        {
          type: "bar",
          name: "Fee Value",
          data: feeValues,
        },
      ],
    };

    this.topClientFeeChart.setOption(this.topClientFeeChartOptions);
  };

  getBottomClientFeesByLimit = () => {
    this.setLoading(true);
    this.feesRepo
      .getClientFeesByLimit(
        this.clientFeeDate,
        this.feesByClientLimit,
        false,
        this.selectedCurrency,
        this.selectedFeeCalculationTypeClientFees,
        this.sortTopBottomClientFeesByNav
      )
      .then((res) => {
        this.bottomClientFees = res.data as Array<any>;
        this.getBottomClientFeeGraph();
        this.bottomClientFeesGridOptions.data = this.bottomClientFees;
      })
      .catch((err) => {
        this.notifyjs("Failed to get bottom client fees " + extractErrorMessage(err));
      })
      .finally(() => {
        this.setLoading(false);
      });
  };

  getBottomClientFeeGraph = () => {
    let clientNames = this.bottomClientFees.map((client) => client.clientName);
    let feeValues;
    if (this.sortTopBottomClientFeesByNav) {
      feeValues = this.bottomClientFees.map((client) =>
        client.marketValue.toFixed(2)
      );
    } else {
      feeValues = this.bottomClientFees.map((client) =>
        client.feeValue.toFixed(2)
      );
    }

    this.bottomClientFeeChartOptions = {
      title: {
        text: `Bottom ${clientNames.length} Client ${
          this.sortTopBottomClientFeesByNav ? "NAVs" : "Fees"
        }`,
        textStyle: {
          fontFamily: "Seravek",
        },
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        formatter: (params) => {
          let feeParam = this.bottomClientFees.filter(
            (feeParam) => feeParam.clientName == params[0].name
          )[0];
          return `Client : ${feeParam.clientName} <br>
                    Client Number : ${feeParam.clientNumber} <br>
                    Fee Value : ${feeParam.feeValue.toLocaleString("en-EN", {
                      style: "currency",
                      currency: this.selectedCurrency,
                    })} <br>
                    NAV : ${feeParam.marketValue.toLocaleString("en-EN", {
                      style: "currency",
                      currency: this.selectedCurrency,
                    })} `;
        },
      },

      grid: {
        left: "3%",
        right: "4%",
        bottom: "3%",
        containLabel: true,
      },
      toolbox: {
        feature: {
          saveAsImage: {
            title: "Save as Image",
          },
          dataZoom: {
            yAxisIndex: "none",
            title: {
              zoom: "Zoom In",
              back: "Back",
            },
          },
          restore: {
            title: "Restore",
          },
        },
      },
      xAxis: {
        type: "category",
        boundaryGap: true,
        data: clientNames,
        barGap: 0,
        axisLabel: {
          rotate: 45,
        },
      },
      yAxis: [
        {
          type: "value",
          name: `${this.sortTopBottomClientFeesByNav ? "NAVs" : "Fees"} `,

          axisLabel: {
            formatter: (params) => {
              return params > 0
                ? `${this.selectedCurrency} ${this.normaliseNumbers(params)}`
                : "";
            },
          },
        },
      ],
      series: [
        {
          type: "bar",
          name: "Fee Value",
          data: feeValues,
        },
      ],
    };

    this.bottomClientFeeChart.setOption(this.bottomClientFeeChartOptions);
  };

  normaliseNumbers = (labelValue) => {
    // Nine Zeroes for Billions
    return Math.abs(Number(labelValue)) >= 1.0e9
      ? Math.abs(Number(labelValue)) / 1.0e9 + "B"
      : // Six Zeroes for Millions
      Math.abs(Number(labelValue)) >= 1.0e6
      ? Math.abs(Number(labelValue)) / 1.0e6 + "M"
      : // Three Zeroes for Thousands
      Math.abs(Number(labelValue)) >= 1.0e3
      ? Math.abs(Number(labelValue)) / 1.0e3 + "K"
      : Math.abs(Number(labelValue));
  };

  exportClientFeesToCSV = ($event: angular.IAngularEvent) => {
    $event.stopPropagation();
    let topClientFees = _.flatten(
      _.map(this.topClientFees, (clientFee) => {
        return _.flatten(
          _.map(clientFee.portfolios, (portfolioFee) => {
            return {
              clientNumber: clientFee.clientNumber,
              clientName: clientFee.clientName,
              currency: this.selectedCurrency,
              totalFeeValue: clientFee.feeValue,
              totalFeeExclusion: clientFee.feeExclusionValue,
              totalMarketValue: clientFee.marketValue,
              accountNumber: portfolioFee.accountNumber.toString(),
              feeType: portfolioFee.feeTypeName,
              feeValue: portfolioFee.feeValue,
              feeExclusion: portfolioFee.feeExclusionValue,
              marketValue: portfolioFee.marketValue,
            };
          })
        );
      })
    );

    let bottomClientFees = _.flatten(
      _.map(this.bottomClientFees, (clientFee) => {
        return _.flatten(
          _.map(clientFee.portfolios, (portfolioFee) => {
            return {
              clientNumber: clientFee.clientNumber,
              clientName: clientFee.clientName,
              currency: this.selectedCurrency,
              totalFeeValue: clientFee.feeValue,
              totalFeeExclusion: clientFee.feeExclusionValue,
              totalMarketValue: clientFee.marketValue,
              accountNumber: portfolioFee.accountNumber.toString(),
              feeType: portfolioFee.feeTypeName,
              feeValue: portfolioFee.feeValue,
              feeExclusion: portfolioFee.feeExclusionValue,
              marketValue: portfolioFee.marketValue,
            };
          })
        );
      })
    );

    exportToCSV(topClientFees, "Top Client Fees", true);
    exportToCSV(bottomClientFees, "Bottom Client Fees", true);
  };
}

export const clientFees: angular.IComponentOptions = {
  template: require("./client-fees.html"),
  controller: ClientFeesController,
  bindings: {
    lastMonth: "<",
    selectedCurrency: "<",
    feeCalulationTypes: "<",
    clientFeeDate: "=",
    showClientFee: "=",
    exportClientFeesRef: "=",
  },
};




// WEBPACK FOOTER //
// ./src/app/fees/fees-components/client-fees/client-fees.ts